import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { apiClient } from '@/api/apiClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calculator, Loader2, RefreshCcw, Lock } from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (value) => `$${(Number(value) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const buildFormFromEmployee = (employee) => ({
  annual_salary: employee?.annual_salary || '',
  filing_status: employee?.filing_status || 'single',
  state: employee?.state_of_residence || employee?.state || '',
  w4_version: employee?.w4_version || 'pre_2020',
  w4_allowances: employee?.w4_allowances || '',
  w4_step3_total: employee?.w4_step3_total || '',
  w4_step2_multiple_jobs: employee?.w4_step2_multiple_jobs || false,
  pre_tax_monthly: ''
});

/**
 * Ad hoc Section 125 calculator, pre-filled from a real employee's stored
 * demographics. Hits the same /proposals/calculate-single endpoint (and thus
 * the same calculation engine) as the admin Proposal tool's Employee Calculator.
 */
export default function EmployeeCalculatorDialog({ open, onOpenChange, employee = null }) {
  const [form, setForm] = useState(buildFormFromEmployee(employee));
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(buildFormFromEmployee(employee));
      setLocked(false);
      setResult(null);
    }
  }, [open, employee]);

  const handleReset = () => {
    setLocked(false);
    setResult(null);
    setForm(buildFormFromEmployee(employee));
  };

  const handleCalculate = async () => {
    if (!form.annual_salary || Number(form.annual_salary) <= 0) {
      toast.error('Enter a valid annual salary');
      return;
    }
    setLoading(true);
    try {
      const response = await apiClient.post('/proposals/calculate-single', {
        annual_salary: Number(form.annual_salary),
        filing_status: form.filing_status,
        state: form.state || undefined,
        w4_version: form.w4_version,
        w4_allowances: Number(form.w4_allowances || 0),
        w4_step3_total: Number(form.w4_step3_total || 0),
        w4_step2_multiple_jobs: form.w4_step2_multiple_jobs,
        pre_tax_monthly: Number(form.pre_tax_monthly || 0),
        employer_id: employee?.employer_id || undefined
      });
      if (!response.data?.eligible) {
        toast.warning('No eligible Section 125 amount for these inputs');
      }
      setResult(response.data);
      setLocked(true);
    } catch (err) {
      console.error('[EmployeeCalculatorDialog] Error:', err);
      toast.error('Calculation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-emerald-500" />
            Employee Calculator
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-slate-500">
            Test this employee's Section 125 scenario. Pre-filled from their profile; uses the same calculation engine as the proposal batch flow.
          </p>

          {locked && (
            <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
              <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-400" />
              <span>Inputs are locked to this calculation. Click "Recalculate with New Inputs" below to start a fresh scenario.</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Annual Salary</Label>
              <Input
                type="number"
                value={form.annual_salary}
                onChange={(e) => setForm({ ...form, annual_salary: e.target.value })}
                disabled={locked}
                placeholder="65000"
              />
            </div>
            <div>
              <Label>Filing Status</Label>
              <Select value={form.filing_status} onValueChange={(v) => setForm({ ...form, filing_status: v })} disabled={locked}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single</SelectItem>
                  <SelectItem value="married">Married</SelectItem>
                  <SelectItem value="head_of_household">Head of Household</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>State</Label>
              <Input
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })}
                disabled={locked}
                placeholder="CA"
                maxLength={2}
              />
            </div>
            <div>
              <Label>W-4 Version</Label>
              <Select value={form.w4_version} onValueChange={(v) => setForm({ ...form, w4_version: v })} disabled={locked}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pre_2020">Pre-2020 (Old W-4)</SelectItem>
                  <SelectItem value="post_2020">Post-2020 (New W-4)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>W-4 Allowances</Label>
              <Input
                type="number"
                value={form.w4_allowances}
                onChange={(e) => setForm({ ...form, w4_allowances: e.target.value })}
                disabled={locked}
              />
            </div>
            <div>
              <Label>Step 3 Credits ($)</Label>
              <Input
                type="number"
                value={form.w4_step3_total}
                onChange={(e) => setForm({ ...form, w4_step3_total: e.target.value })}
                disabled={locked}
              />
            </div>
            <div>
              <Label>Pre-Tax Monthly ($)</Label>
              <Input
                type="number"
                value={form.pre_tax_monthly}
                onChange={(e) => setForm({ ...form, pre_tax_monthly: e.target.value })}
                disabled={locked}
              />
            </div>
            <div className="flex items-center gap-2 pt-5">
              <Switch
                checked={form.w4_step2_multiple_jobs}
                onCheckedChange={(checked) => setForm({ ...form, w4_step2_multiple_jobs: checked })}
                disabled={locked}
              />
              <Label className="cursor-pointer">Multiple Jobs (Step 2)</Label>
            </div>
          </div>

          {result && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 space-y-2">
              {result.eligible ? (
                <>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-slate-500">Section 125 / mo:</span> <span className="font-semibold text-blue-700">{formatCurrency(result.computed_data.section125_monthly)}</span></div>
                    <div><span className="text-slate-500">Your Net / mo:</span> <span className="font-semibold text-emerald-700">{formatCurrency(result.computed_data.net_flex)}</span></div>
                    <div><span className="text-slate-500">Empr Gross / mo:</span> <span className="font-medium">{formatCurrency(result.computed_data.employer_gross)}</span></div>
                    <div><span className="text-slate-500">Fee / mo:</span> <span className="font-medium">{formatCurrency(result.computed_data.fee)}</span></div>
                    <div><span className="text-slate-500">Contribution / mo:</span> <span className="font-medium">{formatCurrency(result.computed_data.contribution)}</span></div>
                    <div><span className="text-slate-500">Employer Net / mo:</span> <span className="font-semibold text-purple-700">{formatCurrency(result.computed_data.employer_net)}</span></div>
                    <div className="col-span-2"><span className="text-slate-500">Annualized:</span> <span className="font-semibold text-purple-700">{formatCurrency(result.computed_data.annualized)}</span></div>
                  </div>
                  <div className="h-px bg-emerald-200" />
                  <div className="grid grid-cols-3 gap-2 text-xs text-slate-600">
                    <div>Federal: {formatCurrency(result.computed_data.federal_tax_savings)}</div>
                    <div>FICA: {formatCurrency(result.computed_data.fica_tax_savings)}</div>
                    <div>State: {formatCurrency(result.computed_data.state_tax_savings)}</div>
                  </div>
                </>
              ) : (
                <p className="text-sm text-amber-700">No eligible Section 125 amount for these inputs.</p>
              )}
            </div>
          )}

          <div className="flex gap-2">
            {locked ? (
              <Button variant="outline" className="flex-1" onClick={handleReset}>
                <RefreshCcw className="w-4 h-4 mr-2" />
                Recalculate with New Inputs
              </Button>
            ) : (
              <Button className="flex-1" onClick={handleCalculate} disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />}
                Calculate
              </Button>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

EmployeeCalculatorDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  employee: PropTypes.object
};
